import * as React from 'react'
import { useQuery } from '@apollo/client'
import parse, { attributesToProps, Element } from 'html-react-parser'
import { assign, each, isEmpty, isNil, map, pick, some } from 'lodash'
import { css } from 'styled-components'
import { useFlag } from 'toggled'

import { analytics } from '~/analytics'
import { Loader, Media, Meta } from '~/components'
import Wysiwyg from '~/components/wysiwyg'
import { userCartQuery, vendorProductQuery } from '~/gql/queries'
import { useIsMobile } from '~/hooks'
import useVariantManager from '~/hooks/use-variant-manager'
import useVendor from '~/hooks/use-vendor'
import { MainLayout } from '~/layouts'
import ProductReviews from '~/modules/reviews/containers/product-review'
import Button from '~/truck/button'
import Icon from '~/truck/icon'
import Label from '~/truck/label'
import theme from '~/truck/theme'
import type { IProduct } from '~/types/products'
import { updateQuantitiesInProducts } from '~/utils'
import { flags, httpStatusCodes, images } from '~/utils/constants'
import type { BreadcrumbItem } from '~/utils/get-schemas'
import isBrowser from '~/utils/is-browser'

import Breadcrumb from './breadcrumb'
import { CTA } from './cta'
import { AboutContainer, ContentContainer, InnerContentContainer, StyledErrorComponent } from './elements'
import Gallery from './gallery'
import { NotAvailable } from './not-available'
import RelatedProducts from './related-products'
import Slider from './slider'

interface ProductScreenProps {
  productSlug: string
  ssrProduct: IProduct | null
}

function Content(props) {
  const { fallbackProduct, activeVariant, isAvailable, selectedPhotoIndex, setSelectedPhotoIndex } = props

  const category = fallbackProduct.mainVariant?.category?.parent

  const subcategory = fallbackProduct.mainVariant?.category

  const route = `/c/${category?.slug}-${category?.encodedId}/${subcategory?.slug}-${subcategory?.encodedId}/`

  const name = activeVariant?.name ?? fallbackProduct.name

  const photos = activeVariant?.photos ?? fallbackProduct.photos

  const sliderPhotos = !isEmpty(photos) ? map(photos, 'url') : [images.PRODUCT_PLACEHOLDER]

  const sliderProps = { alt: name, photos: sliderPhotos }

  const items = [{ label: 'Inicio', route: '/' }, { label: subcategory?.name, route }, { label: name }]

  return (
    <ContentContainer>
      <Media desktop={<Breadcrumb items={items} productIsAvailable={isAvailable} />} />
      <InnerContentContainer>
        <Media
          mobile={<Slider {...sliderProps} />}
          desktop={
            <Gallery
              {...sliderProps}
              selectedPhotoIndex={selectedPhotoIndex}
              setSelectedPhotoIndex={setSelectedPhotoIndex}
            />
          }
        />
        {isAvailable ? <CTA {...props} /> : <NotAvailable {...props} />}
      </InnerContentContainer>
    </ContentContainer>
  )
}

const buttonStyles = css`
  background-color: ${theme.colors.white};
  bottom: ${theme.spacing.compact * 3}px;
  height: ${theme.spacing.comfortable * 2}px;
  left: 0;
  position: absolute;
`

function VariantManager(props) {
  const { product, cart, variants, isAvailable } = props

  const initialValue = { fallbackProduct: product, activeVariant: product }

  const variantManager = useVariantManager(initialValue, variants)

  const vendor = useVendor()

  const isMobile = useIsMobile()

  const hasReviewFF = useFlag(flags.HAS_REVIEW)

  const hasRelatedProducts = useFlag(flags.RELATED_PRODUCTS)

  const { fallbackProduct, activeVariant, onVariantChange, selectedPhotoIndex, setSelectedPhotoIndex } = variantManager

  React.useEffect(() => {
    if (!activeVariant) {
      return
    }

    analytics.viewItem(activeVariant, { vendor })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant?.sku])

  React.useEffect(() => {
    updateQuantitiesInProducts(variants, cart)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackProduct, cart])

  const { description } = product.mainVariant || {}

  const metaData = pick(activeVariant || fallbackProduct, [
    'name',
    'description',
    'seoTitle',
    'seoDescription',
    'photos',
    'sku',
    'slug',
    'stock',
    'pricing',
    'reviewStats',
  ]) as IProduct

  const [clamped, setClamped] = React.useState(true)

  const [showReadMore, setShowReadMore] = React.useState(true)

  const [isEvaluated, setIsEvaluated] = React.useState(false)

  const IMG_REGEX = /<img[^>]*src=('|")([^'"]+)('|")[^>]*>/

  const hasImage = IMG_REGEX.test(description)

  const handleClick = () => setClamped(!clamped)

  const onLoad = () => setIsEvaluated(true)

  const aboutContainerRef = React.useCallback(
    node => {
      if (node && isEvaluated && clamped) {
        setShowReadMore(node.clientHeight < node.scrollHeight)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEvaluated],
  )

  React.useEffect(() => {
    if (!hasImage) {
      setIsEvaluated(true)
    }
  }, [hasImage])

  const category = fallbackProduct.mainVariant?.category?.parent

  const subcategory = fallbackProduct.mainVariant?.category

  const breadcrumbs: BreadcrumbItem[] = []

  if (category) {
    breadcrumbs.push({ name: category.name, url: `/c/${category?.slug}-${category?.encodedId}` })

    if (subcategory) {
      breadcrumbs.push({
        name: subcategory.name,
        url: `/c/${category.slug}-${category.encodedId}/${subcategory.slug}-${subcategory.encodedId}`,
      })
    }
  }

  breadcrumbs.push({ name: product.name })

  return (
    <>
      <Meta.Product {...metaData} breadcrumbs={breadcrumbs} />
      <Content
        product={product}
        fallbackProduct={fallbackProduct}
        activeVariant={activeVariant}
        onVariantChange={onVariantChange}
        variants={variants}
        isAvailable={isAvailable}
        selectedPhotoIndex={selectedPhotoIndex}
        setSelectedPhotoIndex={setSelectedPhotoIndex}
      />
      {description && (
        <AboutContainer
          ref={aboutContainerRef}
          clamped={clamped}
          showReadMore={showReadMore}
          style={{ maxHeight: clamped && isMobile ? `${theme.spacing.relaxed * 13}px` : null }}
        >
          <Label as="h4" $textStyle="h4Semibold">
            Acerca del producto
          </Label>
          <Wysiwyg />
          <Label as="span" className="wysiwyg">
            {parse(description, {
              replace: domNode => {
                const typedDomNode = domNode as Element

                if (typedDomNode.attribs && typedDomNode.name === 'img') {
                  const props = attributesToProps(typedDomNode.attribs)

                  return <img {...props} onLoad={onLoad} />
                }
              },
            })}
          </Label>
          {showReadMore && (
            <Button.Plain css={buttonStyles} onClick={handleClick}>
              <Label $textStyle="h5Semibold" $color="info">
                Ver {clamped ? 'más' : 'menos'}
              </Label>
              &nbsp;
              <Icon type={clamped ? 'caret-down' : 'caret-up'} size="sm" interactive={false} color="info" />
            </Button.Plain>
          )}
        </AboutContainer>
      )}
      {isAvailable && hasRelatedProducts && <RelatedProducts productId={product.mainVariant.id} />}
      {isAvailable && hasReviewFF && <ProductReviews productSlug={product.slug} />}
    </>
  )
}

function ProductScreen(props: ProductScreenProps) {
  const { productSlug, ssrProduct } = props

  const vendor = useVendor()

  const isAvailable = !isNil(ssrProduct) ? ssrProduct.vendor.id === vendor.id : true

  const productResponse = useQuery(vendorProductQuery, {
    skip: (!!ssrProduct && !isBrowser) || !isAvailable,
    variables: {
      slug: productSlug,
    },
  })

  const userCartResponse = useQuery(userCartQuery, {
    skip: !isAvailable,
  })

  const loading = some([userCartResponse, productResponse], 'loading')

  const renderContent = () => {
    const product = productResponse.data?.vendor?.product ?? ssrProduct

    if (!product && isBrowser && loading) {
      return <Loader />
    }

    if (!product) {
      return <StyledErrorComponent statusCode={httpStatusCodes.NOT_FOUND} />
    }

    const cart = userCartResponse.data?.user?.cart

    const allVariants = [product, ...product.variants]

    const setGetters = item => {
      if (item === product) {
        return
      }

      assign(item, {
        get variantsAttributes() {
          return product.variantsAttributes
        },
        get mainVariant() {
          return product.mainVariant
        },
      })
    }

    if (isAvailable) {
      updateQuantitiesInProducts(allVariants, cart)

      each(allVariants, setGetters)
    }

    return (
      /**
       * We need to re-mount when the product was changed and
       * re-initialize the state of the VariantManager
       * component.
       * @url https://github.com/facebook/react/issues/14830#issuecomment-535546574
       */
      <VariantManager
        key={`product-screen-variant-manager-${product.id}`}
        product={product}
        cart={cart}
        variants={allVariants}
        isAvailable={isAvailable}
      />
    )
  }

  return <MainLayout>{renderContent()}</MainLayout>
}

export default ProductScreen
